import { isFunction, isPlainObject } from "lodash";
import { create } from "zustand";

import type {
  BuilderIOTabOptions,
  GalleryTabData,
  GalleryTabOptions,
  MusicTabDataOptions,
  MusicTabOptions,
  ProductTabOptions,
  SKUPageContent,
  SKUPageContentGenericTabIcon,
  SKUPageContentOptionsIntegrations,
  SKUPageContentTabType,
  SKUUniqueContent,
  UniqueTabColumnType,
  UniqueTabOptions,
  WebpageTabOptions,
} from "@/data/types";
import {
  addGalleryItems,
  addPlaylistTracks,
  addTab,
  addUniqueTabColumnMapping,
  deleteTab,
  moveCMSTab,
  moveGalleryItem,
  movePlaylistTrack,
  type PlaylistTrackItem,
  removeGalleryItem,
  removePlaylistTrack,
  removeUniqueTabColumnMapping,
  updateAudioTabOptions,
  updateBuilderIOTabOptions,
  updateGalleryItem,
  updateGalleryTabOptions,
  updateIntegrations,
  updatePlaylistTrack,
  updatePlaylistTracks,
  updateProductTabOptions,
  updateTabDetails,
  updateUniqueTabColumnMapping,
  updateUniqueTabOptions,
  updateWebpageTabOptions,
} from "@/lib/cms";
import { sanitizePageContent } from "@/lib/cms";
import { UploadedFile } from "@/lib/uploads";
import { createSelectors } from "@/state";

import { usePreviewStore } from "./use-preview-store";

interface CMSState {
  pageContent: SKUPageContent | null;
  uniqueContent: SKUUniqueContent | null;
  activeTabIndex: number | undefined;
  setPageContent: (pageContent: SKUPageContent) => void;
  setUniqueContent: (uniqueContent?: SKUUniqueContent | null) => void;
  setActiveTabIndex: (activeTabIndex: number | undefined) => void;
  onMoveTabUp: (tabIndex: number) => void;
  onMoveTabDown: (tabIndex: number) => void;
  onExpandTab: (tabIndex: number) => void;
  onDeleteTab: (tabIndex: number) => void;
  hasUnsavedChanges: boolean;
  hasValidationErrors: boolean;
  setValidationErrors: (hasErrors: boolean) => void;
  onAddTab: (type: SKUPageContentTabType) => void;
  onUpdateAudioTabOptions: (tabIndex: number, options: MusicTabOptions) => void;
  onMovePlaylistTrack: (
    tabIndex: number,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onDeletePlaylistTrack: (tabIndex: number, trackIndex: number) => void;
  onDeleteGalleryItem: (tabIndex: number, itemIndex: number) => void;
  onMoveGalleryItem: (
    tabIndex: number,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onUpdateGalleryTabOptions: (
    tabIndex: number,
    options: GalleryTabOptions,
  ) => void;
  onUpdateBuilderIOTabOptions: (
    tabIndex: number,
    options: BuilderIOTabOptions,
  ) => void;
  onCustomizeTab: (
    tabIndex: number,
    title: string | undefined,
    icon: SKUPageContentGenericTabIcon | undefined,
  ) => void;
  onAddGalleryItems: (tabIndex: number, items: UploadedFile[]) => void;
  onAddPlaylistTracks: (tabIndex: number, tracks: PlaylistTrackItem[]) => void;
  onUpdateIntegrations: (
    integrations: SKUPageContentOptionsIntegrations,
  ) => void;
  onUpdateProductTabOptions: (
    tabIndex: number,
    options: ProductTabOptions,
  ) => void;
  onUpdatePlaylistTrack: (
    tabIndex: number,
    trackIndex: number,
    options: MusicTabDataOptions,
  ) => void;
  onUpdatePlaylistTracks: (
    tabIndex: number,
    updates: Array<{
      trackIndex: number;
      options: Partial<MusicTabDataOptions>;
    }>,
  ) => void;
  onUpdateGalleryItem: (
    tabIndex: number,
    itemIndex: number,
    options: GalleryTabData["options"],
  ) => void;
  onUpdateUniqueTabOptions: (
    tabIndex: number,
    options: UniqueTabOptions,
  ) => void;
  onAddUniqueTabColumnMapping: (tabIndex: number, columnName: string) => void;
  onUpdateUniqueTabColumnMapping: (
    tabIndex: number,
    columnMappingIndex: number,
    values: {
      columnName: string;
      columnType: UniqueTabColumnType;
      isDownloadable: boolean;
    },
  ) => void;
  onRemoveUniqueTabColumnMapping: (
    tabIndex: number,
    columnMappingIndex: number,
  ) => void;
  onUpdateWebpageTabOptions: (
    tabIndex: number,
    options: WebpageTabOptions,
  ) => void;
}

const updatePageContent = <S extends CMSState>(
  state: S,
  predicate: (content: SKUPageContent) => SKUPageContent,
  extra: Partial<S> = {},
): Partial<S> => {
  const conditions = [
    !isPlainObject(state.pageContent),
    !isFunction(predicate),
  ];

  if (conditions.some(Boolean)) {
    return state;
  }

  return {
    // We are sure that pageContent is not null here because a null check is
    // one of the conditions.
    pageContent: predicate(state.pageContent!),
    hasUnsavedChanges: true,
    ...extra,
  };
};

export const useCMSStore = createSelectors(
  create<CMSState>((set) => ({
    pageContent: null,
    uniqueContent: null,
    activeTabIndex: undefined,
    hasUnsavedChanges: false,
    hasValidationErrors: false,
    setValidationErrors: (hasErrors) => {
      set({ hasValidationErrors: hasErrors });
    },
    setPageContent: (content) => {
      set({
        pageContent: sanitizePageContent(content),
        hasUnsavedChanges: false,
      });
    },
    setUniqueContent: (uniqueContent) => {
      set({ uniqueContent });
    },
    setActiveTabIndex: (activeTabIndex) => {
      set({ activeTabIndex });
    },
    onAddTab: (type) => {
      set((state) => {
        return updatePageContent(state, (content) => addTab(content, type));
      });
    },
    onMoveTabUp: (tabIndex) => {
      const { updateOverrides } = usePreviewStore.getState();

      set((state) => {
        const isActiveTab = state.activeTabIndex === tabIndex;

        if (isActiveTab) {
          updateOverrides({ activeTabIndex: tabIndex - 1 });
        }

        const activeTabIndex = isActiveTab
          ? tabIndex - 1
          : state.activeTabIndex;

        return updatePageContent(
          state,
          (content) => moveCMSTab(content, tabIndex, tabIndex - 1),
          { activeTabIndex },
        );
      });
    },
    onMoveTabDown: (tabIndex) => {
      const { updateOverrides } = usePreviewStore.getState();

      set((state) => {
        const isActiveTab = state.activeTabIndex === tabIndex;

        if (isActiveTab) {
          updateOverrides({ activeTabIndex: tabIndex + 1 });
        }

        const activeTabIndex = isActiveTab
          ? tabIndex + 1
          : state.activeTabIndex;

        return updatePageContent(
          state,
          (content) => moveCMSTab(content, tabIndex, tabIndex + 1),
          { activeTabIndex },
        );
      });
    },
    onExpandTab: (tabIndex) => {
      const { updateOverrides } = usePreviewStore.getState();

      set((state) => {
        const isActiveTab = state.activeTabIndex === tabIndex;

        const activeTabIndex = isActiveTab ? undefined : tabIndex;

        updateOverrides({ activeTabIndex: isActiveTab ? 0 : tabIndex });

        return { activeTabIndex };
      });
    },
    onCustomizeTab: (tabIndex, title, icon) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          const payload = {
            tab_title: title,
            tab_icon: icon,
          };

          return updateTabDetails(content, payload, tabIndex);
        });
      });
    },
    onDeleteTab: (tabIndex) => {
      set((state) => {
        const activeTabIndex =
          state.activeTabIndex === tabIndex ? undefined : state.activeTabIndex;

        return updatePageContent(
          state,
          (content: SKUPageContent) => deleteTab(content, tabIndex),
          { activeTabIndex },
        );
      });
    },
    onUpdateAudioTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateAudioTabOptions(content, options, tabIndex);
        });
      });
    },
    onMovePlaylistTrack: (
      tabIndex: number,
      oldIndex: number,
      newIndex: number,
    ) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return movePlaylistTrack(content, tabIndex, oldIndex, newIndex);
        });
      });
    },
    onDeletePlaylistTrack: (tabIndex, trackIndex) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return removePlaylistTrack(content, tabIndex, trackIndex);
        });
      });
    },
    onDeleteGalleryItem: (tabIndex, itemIndex) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return removeGalleryItem(content, tabIndex, itemIndex);
        });
      });
    },
    onMoveGalleryItem: (tabIndex, oldIndex, newIndex) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return moveGalleryItem(content, tabIndex, oldIndex, newIndex);
        });
      });
    },
    onUpdateGalleryTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateGalleryTabOptions(content, options, tabIndex);
        });
      });
    },
    onUpdateBuilderIOTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateBuilderIOTabOptions(content, options, tabIndex);
        });
      });
    },
    onAddGalleryItems: (tabIndex, items) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return addGalleryItems(content, tabIndex, items);
        });
      });
    },
    onAddPlaylistTracks: (tabIndex, tracks) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return addPlaylistTracks(content, tabIndex, tracks);
        });
      });
    },
    onUpdateIntegrations: (integrations) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateIntegrations(content, integrations);
        });
      });
    },
    onUpdateProductTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateProductTabOptions(content, options, tabIndex);
        });
      });
    },
    onUpdatePlaylistTrack: (tabIndex, trackIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updatePlaylistTrack(content, tabIndex, trackIndex, options);
        });
      });
    },
    onUpdatePlaylistTracks: (tabIndex, updates) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updatePlaylistTracks(content, tabIndex, updates);
        });
      });
    },
    onUpdateGalleryItem: (tabIndex, itemIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateGalleryItem(content, tabIndex, itemIndex, options);
        });
      });
    },
    onUpdateUniqueTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateUniqueTabOptions(content, options, tabIndex);
        });
      });
    },
    onAddUniqueTabColumnMapping: (tabIndex, columnName) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return addUniqueTabColumnMapping(content, tabIndex, columnName);
        });
      });
    },
    onUpdateUniqueTabColumnMapping: (tabIndex, columnMappingIndex, values) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateUniqueTabColumnMapping(
            content,
            tabIndex,
            columnMappingIndex,
            values,
          );
        });
      });
    },
    onRemoveUniqueTabColumnMapping: (tabIndex, columnMappingIndex) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return removeUniqueTabColumnMapping(
            content,
            tabIndex,
            columnMappingIndex,
          );
        });
      });
    },
    onUpdateWebpageTabOptions: (tabIndex, options) => {
      set((state) => {
        return updatePageContent(state, (content) => {
          return updateWebpageTabOptions(content, options, tabIndex);
        });
      });
    },
  })),
);
